/*
 * The home page's footer.
 *
 * ── Why not Footer.tsx ─────────────────────────────────────────────────────
 *
 * It is 1062 lines, it is imported by 55 other files, and it contains two
 * divergent footers — a desktop one and a mobile one whose link sets do not
 * match — shown and hidden by CSS with both always in the DOM. Changing its
 * markup to suit one page is a 55-page blast radius.
 *
 * ── What changed from it, and why ──────────────────────────────────────────
 *
 * The LINKS are lifted from it; the grouping is the design's. The live footer
 * files Elite Club, Whales Club, Testimonials and Back to Community under
 * Company, which is why that column runs eleven long and nothing else runs
 * past five. Split into Company and Community, four columns balance.
 *
 * DROPPED, per the design note: the Platform and Products columns. They list
 * Hive, Shop, XNFT, XUSD, Gift Cards, Greeting Cards and $1 Bitcoin — none of
 * which are among the sixteen — and omit ten that are. The page above already
 * indexes all sixteen with their marks; repeating a shorter, wrong version of
 * the same list is the bug, not the fix.
 *
 * FIXED while moving: the live footer hardcodes `https://indexx.ai/...` for
 * its OWN routes. On test that carries a signed-in tester out of the test
 * estate and onto production mid-session. Same-origin links are relative here
 * and routed client-side; only genuinely external ones are absolute.
 *
 * ALSO: the live Telegram icon carries alt="reddit", and the copyright reads
 * 2023.
 */
import { Link } from "react-router-dom";
import lockup from "../../assets/header-icons/indexx-logo-v3-dark.svg";
import s from "./landing.module.css";

/** Same origin — routed, and environment-correct by construction. */
const INTERNAL: Record<string, { label: string; to: string }[]> = {
  Company: [
    { label: "About", to: "/about" },
    { label: "How it works", to: "/how-master-login-works" },
    { label: "Blog", to: "/indexx-exchange/blog" },
    { label: "Vlog", to: "/indexx-exchange/vlog" },
    { label: "Careers", to: "/indexx-exchange/careers" },
    { label: "Contact", to: "/contact" },
  ],
  Community: [
    { label: "Back to Community", to: "/back-to-community" },
    { label: "Elite Club", to: "/elite-club" },
    { label: "Whales Club", to: "/whales-club" },
  ],
};

/** Off-origin, so absolute. */
const EXTERNAL: Record<string, { label: string; href: string }[]> = {
  Program: [
    { label: "Affiliate Program", href: "https://lotto.indexx.ai/affiliate" },
    { label: "Hive", href: "https://hive.indexx.ai/" },
  ],
  Wallet: [
    {
      label: "Wallet Extension",
      href:
        "https://chromewebstore.google.com/detail/indexx-wallet/fpibioaihcagphbidhodidjbnclocgll?hl=en",
    },
    { label: "Wallet Web", href: "https://wallet.indexx.ai/wallets/crypto-wallet" },
  ],
};

const SOCIAL = [
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=100086225564460" },
  { label: "Instagram", href: "https://www.instagram.com/indexx_ai/" },
  { label: "Telegram", href: "https://t.me/indexxai" },
  { label: "Twitter", href: "https://twitter.com/Indexx_ai" },
  { label: "YouTube", href: "https://www.youtube.com/channel/UCDBjhB2zS-WHArfeEISJLrQ" },
];

export default function LandingFooter() {
  return (
    /* <footer> + <ul>, never <nav>: the global header stylesheet targets the
       bare `nav` type selector with position:fixed and a dark background. */
    <footer className={`${s.shell} ${s.footer}`}>
      <div className={s.footerCols}>
        <div className={s.footerBrand}>
          <img src={lockup} alt="indexx.ai" className={s.footerLockup} />
          <p className={s.footerTag}>One account. One key. Sixteen products.</p>
        </div>

        {Object.entries(INTERNAL).map(([title, links]) => (
          <div key={title}>
            <p className={s.footerColTitle}>{title}</p>
            <ul className={s.footerList}>
              {links.map((l) => (
                <li key={l.label}>
                  <Link className={s.footerLink} to={l.to}>{l.label}</Link>
                </li>
              ))}
              {title === "Community"
                ? SOCIAL.map((l) => (
                    <li key={l.label}>
                      <a
                        className={s.footerLink}
                        href={l.href}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))
                : null}
            </ul>
          </div>
        ))}

        {Object.entries(EXTERNAL).map(([title, links]) => (
          <div key={title}>
            <p className={s.footerColTitle}>{title}</p>
            <ul className={s.footerList}>
              {links.map((l) => (
                <li key={l.label}>
                  <a
                    className={s.footerLink}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className={s.footerBar}>
        <span>© {new Date().getFullYear()} Indexx. All rights reserved.</span>
        <span className={s.footerBarLinks}>
          <Link className={s.footerLink} to="/indexx-exchange/legal/privacypolicy">
            Privacy Policy
          </Link>
          <Link className={s.footerLink} to="/indexx-exchange/legal/termsandconditions">
            Terms of Service
          </Link>
          <Link className={s.footerLink} to="/indexx-exchange/legal">
            Legal
          </Link>
        </span>
      </div>
    </footer>
  );
}
